import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { trackClientEvent } from 'analytics/analytics'
import type { SnowplowClientEventParams } from 'analytics/snowplow/events'
import { CONSENT_CHANGED_EVENT } from '../consent/changeEvents'
import { canUseConsentPurposeOnClient } from '../consent/waitingForConsent'

const INACTIVITY_THRESHOLDS_SECONDS = [150, 180, 210, 240, 270, 300] as const
const ACTIVITY_EVENTS = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'wheel', 'touchstart', 'touchmove'] as const
const LISTENER_OPTIONS = { passive: true, capture: true } as const

/** Observation only: no modal, navigation, or other customer-facing action. */
export default function usePassiveDepartureTracking() {
  const { key, pathname } = useLocation()

  useEffect(() => {
    const reportedThresholds = new Set<number>()
    let reportedTopExit = false
    let lastActivityAt = performance.now()
    let timeoutId: number | undefined

    function canObserve() {
      return document.visibilityState === 'visible' && canUseConsentPurposeOnClient('analytics')
    }

    function report(subject: string, action: string) {
      const event = {
        category: 'passive_departure',
        subject,
        action,
        type: 'nonInteraction',
      } satisfies SnowplowClientEventParams
      trackClientEvent(event)
    }

    function scheduleNextThreshold() {
      window.clearTimeout(timeoutId)
      if (!canObserve()) return

      const nextThreshold = INACTIVITY_THRESHOLDS_SECONDS.find(seconds => !reportedThresholds.has(seconds))
      if (nextThreshold === undefined) return

      const remaining = nextThreshold * 1000 - (performance.now() - lastActivityAt)
      timeoutId = window.setTimeout(checkInactivity, Math.max(remaining, 0))
    }

    function checkInactivity() {
      if (!canObserve()) return
      const inactiveForMs = performance.now() - lastActivityAt
      for (const seconds of INACTIVITY_THRESHOLDS_SECONDS) {
        if (inactiveForMs >= seconds * 1000 && !reportedThresholds.has(seconds)) {
          reportedThresholds.add(seconds)
          report('inactivity_threshold_reached', seconds.toString())
        }
      }
      scheduleNextThreshold()
    }

    // High-frequency activity only updates the timestamp. The pending timer
    // recomputes its remaining delay when it fires.
    function noteActivity() {
      lastActivityAt = performance.now()
    }

    function resetInactivity() {
      noteActivity()
      scheduleNextThreshold()
    }

    function handleMouseOut(event: MouseEvent) {
      if (
        reportedTopExit ||
        event.relatedTarget !== null || event.clientY > 0 ||
        event.clientX < 0 || event.clientX >= window.innerWidth ||
        !canObserve() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches
      ) return

      reportedTopExit = true
      report('desktop_top_edge_exit', 'detected')
    }

    ACTIVITY_EVENTS.forEach(event => document.addEventListener(event, noteActivity, LISTENER_OPTIONS))
    document.addEventListener('visibilitychange', resetInactivity)
    document.addEventListener('mouseout', handleMouseOut)
    window.addEventListener(CONSENT_CHANGED_EVENT, resetInactivity)
    scheduleNextThreshold()

    return () => {
      window.clearTimeout(timeoutId)
      ACTIVITY_EVENTS.forEach(event => document.removeEventListener(event, noteActivity, LISTENER_OPTIONS))
      document.removeEventListener('visibilitychange', resetInactivity)
      document.removeEventListener('mouseout', handleMouseOut)
      window.removeEventListener(CONSENT_CHANGED_EVENT, resetInactivity)
    }
  }, [key, pathname])
}
